/**
 * HttpRepository — fetch + envelope unwrap + Zod parse (plan §1.1 · naming §3.5).
 *
 * search() prefetches the universe once per asOf and runs core searchSecurities
 * (plan §6.6 · DatasetRepository port) — never /search per keystroke.
 */
import { z } from 'zod';

import {
  API_LIMIT_MAX,
  DashboardPayloadSchema,
  DatasetMetaSchema,
  DiversificationPayloadSchema,
  PriceSeriesSchema,
  SearchQuerySchema,
  SecurityProfileSchema,
  SecuritySchema,
  SectorDetailSchema,
  type DashboardQuery,
  type DatasetMeta,
  type SearchQuery,
  type SectorId,
  type SectorQuery,
  type Security,
  type SeriesQuery,
  type Ticker,
} from '@meridian/contracts';
import { searchSecurities, type DatasetRepository } from '@meridian/core';

import { buildQuery, httpGetJson } from './http-get';

export interface HttpRepositoryOptions {
  readonly baseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

const listSchema = z.array(SecuritySchema);

type GetJson = <T>(
  path: string,
  schema: z.ZodType<T>,
  notFound?: { readonly resource: 'security' | 'sector'; readonly id: string },
) => Promise<T>;

interface PendingUniverse {
  readonly asOf: string;
  readonly rows: Promise<readonly Security[]>;
}

function pendingAfterSettlement(
  current: PendingUniverse | undefined,
  rows: Promise<readonly Security[]>,
): PendingUniverse | undefined {
  return current?.rows === rows ? undefined : current;
}

function createUniverseCache(
  get: GetJson,
  getMeta: () => Promise<DatasetMeta>,
): () => Promise<readonly Security[]> {
  let cache: { readonly asOf: string; readonly rows: readonly Security[] } | undefined;
  const pendingState: { value: PendingUniverse | undefined } = { value: undefined };
  return async () => {
    const meta = await getMeta();
    const asOf = meta.asOf ?? '';
    if (cache?.asOf === asOf) return cache.rows;
    if (pendingState.value?.asOf === asOf) return pendingState.value.rows;
    const size = meta.universeSize > 0 ? meta.universeSize : API_LIMIT_MAX;
    const limit = Math.min(size, API_LIMIT_MAX);
    const rows = get(
      `/securities${buildQuery({ window: '1Y', index: 'ALL', sort: 'ticker', dir: 'asc', limit })}`,
      listSchema,
    ).then((value) => {
      cache = { asOf, rows: value };
      return value;
    });
    pendingState.value = { asOf, rows };
    try {
      return await rows;
    } finally {
      pendingState.value = pendingAfterSettlement(pendingState.value, rows);
    }
  };
}

function createMetaLoader(get: GetJson): {
  readonly getMeta: () => Promise<DatasetMeta>;
  readonly refreshMeta: () => Promise<DatasetMeta>;
  readonly loadUniverse: () => Promise<readonly Security[]>;
} {
  const getMeta = (): Promise<DatasetMeta> => get('/meta', DatasetMetaSchema);
  const loadUniverse = createUniverseCache(get, getMeta);
  return { getMeta, refreshMeta: getMeta, loadUniverse };
}

function buildRepository(get: GetJson) {
  const metaLoader = createMetaLoader(get);
  return {
    getMeta: metaLoader.refreshMeta,
    getDashboard: (query: DashboardQuery) =>
      get(
        `/dashboard${buildQuery({ window: query.window, index: query.index })}`,
        DashboardPayloadSchema,
      ),
    getSector: (id: SectorId, query: SectorQuery) =>
      get(
        `/sectors/${encodeURIComponent(id)}${buildQuery({ window: query.window, index: query.index })}`,
        SectorDetailSchema,
        { resource: 'sector', id },
      ),
    getSecurity: (ticker: Ticker) =>
      get(`/securities/${encodeURIComponent(ticker)}`, SecurityProfileSchema, {
        resource: 'security',
        id: ticker,
      }),
    getSeries: (ticker: Ticker, query: SeriesQuery) =>
      get(
        `/securities/${encodeURIComponent(ticker)}/series${buildQuery({ window: query.window })}`,
        PriceSeriesSchema,
        { resource: 'security', id: ticker },
      ),
    getPeers: (ticker: Ticker, query: SeriesQuery) =>
      get(
        `/securities/${encodeURIComponent(ticker)}/peers${buildQuery({ window: query.window })}`,
        listSchema,
        { resource: 'security', id: ticker },
      ),
    getDiversifiers: (ticker: Ticker, query: SeriesQuery) =>
      get(
        `/securities/${encodeURIComponent(ticker)}/diversifiers${buildQuery({ window: query.window })}`,
        DiversificationPayloadSchema,
        { resource: 'security', id: ticker },
      ),
    search: async (query: SearchQuery) => {
      const parsed = SearchQuerySchema.parse(query);
      const universe = await metaLoader.loadUniverse();
      return searchSecurities(universe, parsed.q, parsed.limit);
    },
  } satisfies DatasetRepository;
}

export function createHttpRepository(options: HttpRepositoryOptions): DatasetRepository {
  const base = options.baseUrl.replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? fetch;
  const get: GetJson = (path, schema, notFound) =>
    httpGetJson(fetchImpl, `${base}${path}`, schema, notFound);
  return buildRepository(get);
}
